import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './RecordSession.css';

function RecordSession() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [patientId, setPatientId] = useState('');
  const [patientInfo, setPatientInfo] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: ''
  });
  const [consent, setConsent] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingMode, setRecordingMode] = useState('live'); // 'live' or 'upload'
  const [transcript, setTranscript] = useState('');
  const [medicalEntities, setMedicalEntities] = useState({
    symptoms: [],
    diagnoses: [],
    medications: [],
    procedures: [],
    vitals: []
  });
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showResults, setShowResults] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptPiece = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcriptPiece + ' ';
          } else {
            interimTranscript += transcriptPiece;
          }
        }
        
        if (finalTranscript) {
          setTranscript(prev => prev + finalTranscript);
          extractMedicalInfo(finalTranscript);
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          console.log('No speech detected');
        }
      };
      
      recognitionRef.current = recognition;
    }

    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Already stopped
        }
      }
    };
  }, []);

  const extractMedicalInfo = (text) => {
    console.log('Extracting from text:', text);
    const lowerText = text.toLowerCase();
    
    // Extract symptoms - more comprehensive
    const symptomKeywords = [
      'pain', 'ache', 'headache', 'migraine', 'fever', 'cough', 'cold',
      'nausea', 'vomit', 'dizzy', 'dizziness', 'fatigue', 'tired', 'weakness',
      'shortness of breath', 'breathing', 'chest pain', 'abdominal pain', 
      'stomach pain', 'sore throat', 'congestion', 'runny nose',
      'vomiting', 'diarrhea', 'rash', 'swelling', 'bleeding', 'bruising',
      'chills', 'sweating', 'itching', 'burning', 'tingling', 'numbness'
    ];
    const foundSymptoms = symptomKeywords.filter(symptom => lowerText.includes(symptom));
    console.log('Found symptoms:', foundSymptoms);
    
    // Extract medications - more comprehensive
    const medicationPattern = /\b(aspirin|ibuprofen|acetaminophen|tylenol|advil|motrin|aleve|lipitor|atorvastatin|metformin|glucophage|lisinopril|zestril|amlodipine|norvasc|omeprazole|prilosec|albuterol|proventil|gabapentin|neurontin|losartan|cozaar|metoprolol|lopressor|hydrochlorothiazide|hctz|amoxicillin|amoxil|azithromycin|zithromax|levothyroxine|synthroid|simvastatin|zocor|prednisone|warfarin|coumadin)\b/gi;
    const foundMedications = text.match(medicationPattern) || [];
    console.log('Found medications:', foundMedications);
    
    // Extract vitals - improved patterns
    const vitals = [];
    
    // Blood pressure
    const bpMatches = text.matchAll(/(\d{2,3})\s*(?:over|\/)\s*(\d{2,3})/gi);
    for (const match of bpMatches) {
      vitals.push(`BP: ${match[1]}/${match[2]}`);
    }
    
    // Temperature
    const tempMatches = text.matchAll(/(\d{2,3}(?:\.\d)?)\s*(?:degrees?|°)\s*([CF])?/gi);
    for (const match of tempMatches) {
      vitals.push(`Temp: ${match[0]}`);
    }
    
    // Heart rate
    const hrMatches = text.matchAll(/(\d{2,3})\s*(?:bpm|beats?\s*per\s*minute)/gi);
    for (const match of hrMatches) {
      vitals.push(`HR: ${match[0]}`);
    }
    
    // Weight
    const weightMatches = text.matchAll(/(\d{2,3})\s*(?:lbs?|pounds?|kg|kilograms?)/gi);
    for (const match of weightMatches) {
      vitals.push(`Weight: ${match[0]}`);
    }
    
    console.log('Found vitals:', vitals);
    
    // Extract diagnoses
    const diagnosisKeywords = [
      'diabetes', 'diabetic', 'hypertension', 'high blood pressure',
      'asthma', 'copd', 'depression', 'anxiety', 'arthritis',
      'migraine', 'pneumonia', 'bronchitis', 'flu', 'influenza',
      'covid', 'coronavirus', 'infection', 'fracture', 'broken bone',
      'hyperlipidemia', 'high cholesterol', 'heart disease', 'stroke'
    ];
    const foundDiagnoses = diagnosisKeywords.filter(diagnosis => lowerText.includes(diagnosis));
    console.log('Found diagnoses:', foundDiagnoses);
    
    // Only update if we found something
    if (foundSymptoms.length > 0 || foundMedications.length > 0 || vitals.length > 0 || foundDiagnoses.length > 0) {
      setMedicalEntities(prev => ({
        symptoms: [...new Set([...prev.symptoms, ...foundSymptoms])],
        medications: [...new Set([...prev.medications, ...foundMedications.map(m => m.toLowerCase())])],
        vitals: [...new Set([...prev.vitals, ...vitals])],
        diagnoses: [...new Set([...prev.diagnoses, ...foundDiagnoses])],
        procedures: prev.procedures
      }));
      console.log('Medical entities updated!');
    }
  };

  const handleStartSession = async () => {
    if (!consent) {
      alert('Patient consent is required');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('http://localhost:5000/api/sessions/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: patientId,
          patient_info: patientInfo,
          consent_given: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        setStep(2);
      } else {
        const error = await response.json();
        alert(`Failed to start session: ${error.error}`);
      }
    } catch (error) {
      console.log('Backend not available, using demo mode');
      // Mock session ID for demo
      setSessionId(`DEMO-${Date.now()}`);
      setStep(2);
    }
  };

  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      
      // Start speech recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
          setIsTranscribing(true);
        } catch (error) {
          console.error('Speech recognition error:', error);
        }
      }
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Create a file from the blob
        const file = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
        setAudioFile(file);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        
        // Clear timer
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
      
      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone. Please grant microphone permissions and try again.');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
    
    // Stop speech recognition
    if (recognitionRef.current && isTranscribing) {
      try {
        recognitionRef.current.stop();
        setIsTranscribing(false);
      } catch (error) {
        console.error('Error stopping recognition:', error);
      }
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFile(file);
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUploadAndProcess = async () => {
    if (!audioFile) {
      alert('Please select an audio file');
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('auth_token');
      
      // Upload audio
      const formData = new FormData();
      formData.append('audio', audioFile);
      
      const uploadResponse = await fetch(
        `http://localhost:5000/api/sessions/${sessionId}/upload-audio`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Audio upload failed');
      }

      // Process session
      const processResponse = await fetch(
        `http://localhost:5000/api/sessions/${sessionId}/process`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (processResponse.ok) {
        alert('Session processed successfully!');
        navigate(`/session/${sessionId}`);
      } else {
        throw new Error('Processing failed');
      }
    } catch (error) {
      console.log('Backend not available, demo mode:', error.message);
      alert('Demo mode: Recording saved with transcript and medical info!');
      setTimeout(() => navigate('/'), 1500);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="record-container">
      <div className="record-box">
        <h1>🎙️ Record Clinical Session</h1>

        {step === 1 && (
          <div className="step-content">
            <h2>Step 1: Patient Information & Consent</h2>
            
            <div className="form-group">
              <label>Patient ID</label>
              <input
                type="text"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                placeholder="PATIENT-001"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={patientInfo.first_name}
                  onChange={(e) => setPatientInfo({...patientInfo, first_name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={patientInfo.last_name}
                  onChange={(e) => setPatientInfo({...patientInfo, last_name: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  value={patientInfo.date_of_birth}
                  onChange={(e) => setPatientInfo({...patientInfo, date_of_birth: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Gender</label>
                <select
                  value={patientInfo.gender}
                  onChange={(e) => setPatientInfo({...patientInfo, gender: e.target.value})}
                  required
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="consent-section">
              <h3>HIPAA Consent & Recording Agreement</h3>
              <div className="consent-box">
                <p>I hereby consent to:</p>
                <ul>
                  <li>The recording of this clinical consultation</li>
                  <li>The processing of conversation audio into text transcripts</li>
                  <li>The automated extraction and analysis of medical information</li>
                  <li>The storage of clinical notes in encrypted HIPAA-compliant systems</li>
                </ul>
                <p>All data is encrypted and protected under HIPAA regulations.</p>
              </div>
              <label className="consent-checkbox">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>I consent to the above terms</span>
              </label>
            </div>

            <button 
              className="btn-primary"
              onClick={handleStartSession}
              disabled={!consent}
            >
              Continue to Recording
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="step-content">
            <h2>Step 2: Record Conversation</h2>
            <p className="session-id">Session ID: {sessionId}</p>

            <div className="mode-selector">
              <button 
                className={`mode-btn ${recordingMode === 'live' ? 'active' : ''}`}
                onClick={() => setRecordingMode('live')}
              >
                🎙️ Live Recording
              </button>
              <button 
                className={`mode-btn ${recordingMode === 'upload' ? 'active' : ''}`}
                onClick={() => setRecordingMode('upload')}
              >
                📁 Upload File
              </button>
            </div>

            {recordingMode === 'live' && (
              <div className="recording-section">
                {!recording && !audioFile && (
                  <div className="recording-controls">
                    <div className="microphone-icon">🎤</div>
                    <h3>Record Conversation</h3>
                    <p>Click the button below to start recording the doctor-patient conversation</p>
                    <p className="info-note">✨ Real-time transcription and medical info extraction enabled</p>
                    
                    {/* Test button to verify extraction works */}
                    <button 
                      className="btn-secondary-small"
                      onClick={() => {
                        const testText = "Patient complains of headache and fever. Blood pressure is 120 over 80. Currently taking aspirin. Diagnosed with hypertension.";
                        setTranscript(testText);
                        extractMedicalInfo(testText);
                        alert('Test extraction completed! Check console for logs.');
                      }}
                      style={{ marginBottom: '10px' }}
                    >
                      🧪 Test Extraction
                    </button>
                    
                    <button 
                      className="btn-record-start"
                      onClick={startRecording}
                    >
                      ⏺ Start Recording
                    </button>
                    <p className="recording-note">Make sure your microphone is enabled</p>
                  </div>
                )}

                {recording && (
                  <div className="recording-active">
                    <div className="recording-indicator">
                      <span className="pulse"></span>
                      <span className="recording-text">RECORDING</span>
                    </div>
                    <div className="recording-timer">{formatTime(recordingTime)}</div>
                    <div className="waveform">
                      <span className="bar"></span>
                      <span className="bar"></span>
                      <span className="bar"></span>
                      <span className="bar"></span>
                      <span className="bar"></span>
                    </div>
                    <button 
                      className="btn-record-stop"
                      onClick={stopRecording}
                    >
                      ⏹ Stop Recording
                    </button>
                    <p className="recording-note">Recording in progress...</p>
                    
                    {/* Real-time Transcript */}
                    {transcript && (
                      <div className="live-transcript">
                        <h4>📝 Live Transcript</h4>
                        <div className="transcript-box">
                          {transcript}
                        </div>
                      </div>
                    )}
                    
                    {/* Extracted Medical Information */}
                    {(medicalEntities.symptoms.length > 0 || 
                      medicalEntities.medications.length > 0 || 
                      medicalEntities.diagnoses.length > 0 ||
                      medicalEntities.vitals.length > 0) && (
                      <div className="medical-entities">
                        <h4>🏥 Extracted Medical Information</h4>
                        
                        {medicalEntities.symptoms.length > 0 && (
                          <div className="entity-group">
                            <strong>Symptoms:</strong>
                            <div className="entity-tags">
                              {medicalEntities.symptoms.map((symptom, idx) => (
                                <span key={idx} className="entity-tag symptom">{symptom}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {medicalEntities.medications.length > 0 && (
                          <div className="entity-group">
                            <strong>Medications:</strong>
                            <div className="entity-tags">
                              {medicalEntities.medications.map((med, idx) => (
                                <span key={idx} className="entity-tag medication">{med}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {medicalEntities.diagnoses.length > 0 && (
                          <div className="entity-group">
                            <strong>Diagnoses:</strong>
                            <div className="entity-tags">
                              {medicalEntities.diagnoses.map((diag, idx) => (
                                <span key={idx} className="entity-tag diagnosis">{diag}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {medicalEntities.vitals.length > 0 && (
                          <div className="entity-group">
                            <strong>Vitals:</strong>
                            <div className="entity-tags">
                              {medicalEntities.vitals.map((vital, idx) => (
                                <span key={idx} className="entity-tag vital">{vital}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {!recording && audioFile && (
                  <div className="recording-complete">
                    <span className="success-icon">✓</span>
                    <h3>Recording Complete</h3>
                    <p>Duration: {formatTime(recordingTime)}</p>
                    <p className="file-size">{(audioFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    
                    {transcript && (
                      <div className="summary-section">
                        <h4>Final Transcript</h4>
                        <div className="transcript-summary">
                          {transcript}
                        </div>
                      </div>
                    )}
                    
                    <button 
                      className="btn-secondary-small"
                      onClick={() => {
                        setAudioFile(null);
                        setRecordingTime(0);
                        setTranscript('');
                        setMedicalEntities({
                          symptoms: [],
                          diagnoses: [],
                          medications: [],
                          procedures: [],
                          vitals: []
                        });
                      }}
                    >
                      🔄 Record Again
                    </button>
                  </div>
                )}
              </div>
            )}

            {recordingMode === 'upload' && (
              <div className="upload-section">
                <p>Upload a pre-recorded audio file (.wav, .mp3, .m4a, .webm)</p>
                
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="audio-upload"
                />
                <label htmlFor="audio-upload" className="btn-upload">
                  📁 Choose Audio File
                </label>
                
                {audioFile && (
                  <div className="file-info">
                    <p>✓ File selected: {audioFile.name}</p>
                    <p>Size: {(audioFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                )}
              </div>
            )}

            {audioFile && (
              <div className="action-buttons">
                <button 
                  className="btn-primary-large"
                  onClick={handleUploadAndProcess}
                  disabled={processing}
                >
                  {processing ? '⏳ Processing...' : '🚀 Upload & Process'}
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => navigate('/')}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RecordSession;
